import type { VirtualElement as IVirtualElement } from '@popperjs/core';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { useFloatingLayerContext } from '../../FloatingManager';
import styled from 'styled-components';

const DEFAULT_TRANSFORM_OFFSET = [0, 0];
const DEFAULT_TRANSFORM_CSS_STRING = `translate(${DEFAULT_TRANSFORM_OFFSET[0]}px, ${DEFAULT_TRANSFORM_OFFSET[1]}px)`;
// by some unknown reason, in some computer, popper uses translate3d instead of translate
// more details below
const DEFAULT_TRANSFORM3D_CSS_STRING =
  `translate3d(${DEFAULT_TRANSFORM_OFFSET[0]}px,` +
  ` ${DEFAULT_TRANSFORM_OFFSET[1]}px, 0)`;

const Container = styled.div`
  display: none;
`;
export interface PopperPortalProps {
  active?: boolean;
  children: ReactNode;
}

class VirtualElement implements IVirtualElement {
  private rect = {
    width: 0,
    height: 0,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    x: 0,
    y: 0,
    toJSON() {
      return this;
    },
  };

  update(x: number, y: number) {
    this.rect.y = y;
    this.rect.top = y;
    this.rect.bottom = y;

    this.rect.x = x;
    this.rect.left = x;
    this.rect.right = x;
  }

  getBoundingClientRect(): DOMRect {
    return this.rect;
  }
}

const POPPER_OPTIONS: Parameters<typeof usePopper>[2] = {
  placement: 'right-start',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: DEFAULT_TRANSFORM_OFFSET,
      },
    },
  ],
};

/**
 * https://github.com/recharts/recharts/issues/2458
 * This component is used to handle the rechart tooltip cannot display correctly
 * when wrapped within 3rd party library, like swiper/react or dndKit who needs to create
 * a transform layer to handle the DOM element.
 *
 * The solution is to create a new div and append it to the body, and use the Poppper to
 * position the tooltip.
 *
 * This solution could be removed after Recharts v3.0 is released as what they mentioned in the issue.
 */
export default function PopperPortal({
  active = true,
  children,
}: PopperPortalProps) {
  const [portalElement, setPortalElement] = useState<HTMLDivElement>();
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>();
  const virtualElementRef = useRef<VirtualElement>(new VirtualElement());

  const { styles, attributes, update } = usePopper(
    virtualElementRef.current,
    popperElement,
    POPPER_OPTIONS,
  );

  const zIndex = useFloatingLayerContext();

  useEffect(() => {
    const el = document.createElement('div');
    document.body.appendChild(el);
    setPortalElement(el);
    return () => el.remove();
  }, []);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const { clientX: x, clientY: y } = event;
      virtualElementRef.current.update(x + 15, y + 10);
      if (!active) {
        return;
      }
      update?.();
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [active, update]);

  useEffect(() => {
    update?.();
  }, [update]);

  if (!portalElement) {
    return null;
  }

  return createPortal(
    <Container
      ref={setPopperElement}
      {...attributes.popper}
      style={{
        ...styles.popper,
        zIndex,
        display:
          active &&
          // Hacking solution to prevent the tooltip from flashing when the transform is not applied
          // it usually happens when switching rapidly between two types of tooltips
          styles.popper.transform &&
          styles.popper.transform !== DEFAULT_TRANSFORM_CSS_STRING &&
          styles.popper.transform !== DEFAULT_TRANSFORM3D_CSS_STRING
            ? 'block'
            : 'none',
      }}
    >
      {children}
    </Container>,
    portalElement,
  );
}
